/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import React from "react";
import { Link } from "react-router-dom";
import BASELoading from "../../../../../../../@BASE/core/BASELoading";
import BASESvgIcon from "../../../../../../../@BASE/core/BASESvgIcon";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";

import MediaAttribution from "../components/MediaAttribution";
import Instructions from "../components/Instructions";
import EMentor from "../components/EMentor";

const PEInteractiveListening = (props) => {
  const { data, dir, ins = [], course, comments = [] } = props;
  const tl = `${dir === "rtl" ? "text-18" : "text-14"} ${course?.language?.acronym}`.trim();

  if (!data) {
    return <BASELoading />;
  }

  // filer comments by component_id
  const componentCommentsCount = (component) => {
    return comments.filter(
      (comment) =>
        comment.component === component && comment.internality !== true
    ).length;
  };
  // format time for 00
  const formatTime = (time) => {
    if (time < 10) {
      return `0${time}`;
    }
    return time;
  };

  return (
    <>
      <style>
        {`
          .wysiwyg p {
            margin: 0px;
						margin-bottom: 1.5rem;
          }
        `}
      </style>
      <div>
        <Typography className="text-16 font-bold p-16 pb-0">
          Page Instructions:
        </Typography>

        <Instructions
          ins={ins}
          course={course}
          dir={dir}
          tl={tl}
          componentCommentsCount={componentCommentsCount}
        />
        <Typography className="text-16 font-bold p-16 pb-0">
          Page Content:
        </Typography>

        <Paper elevation={0} variant="outlined" square className="p-16 m-16">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className="text-16 font-bold m-0">Video:</Typography>
              <Divider className="my-16" />
            </Grid>
            <Grid item xs={12}>
              <Grid item xs={12}>
                <MediaAttribution
                  attribution={data.media}
                  timestamp={{
                    start_minute: data.core.start_minute,
                    end_minute: data.core.end_minute,
                    start_second: data.core.start_second,
                    end_second: data.core.end_second,
                  }}
                  tl={tl}
                  dir={dir}
                  cc={data.cc}
                />
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        {data.cc.length !== 0 && (
          <>
            <Typography className="text-16 font-bold p-16 px-32 pb-0">
              Closed Captions:
            </Typography>
            {data.cc.map((cc, index) => (
              <Paper
                key={cc.id}
                elevation={0}
                variant="outlined"
                square
                className="p-16 pb-32 m-16"
              >
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography className={`${tl} font-bold m-0`} dir={dir}>
                      {cc.tl_text}
                    </Typography>
                    <Divider className="my-16" />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={0.5}>
                      <Grid item xs={2}>
                        <Typography className="text-14 font-semibold m-0">
                          Time Stamp:
                        </Typography>
                      </Grid>
                      <Grid item xs={10}>
                        <Typography className="text-14 m-0">
                          {cc.start_minute}:
                          {cc.start_second < 10
                            ? `0${cc.start_second}`
                            : cc.start_second}{" "}
                          - {cc.end_minute}:
                          {cc.end_second < 10
                            ? `0${cc.end_second}`
                            : cc.end_second}{" "}
                          (from trimmed media)
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </>
        )}

        {data.focus_points.length !== 0 && (
          <>
            <Typography className="text-16 font-bold p-16 px-32 pb-0">
              Focus Points:
            </Typography>
            {data.focus_points.map((data, index) => (
              <Paper
                key={data.linked_item}
                elevation={0}
                variant="outlined"
                square
                className="p-16 pb-32 m-16"
              >
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography className={`${tl} font-bold m-0`} dir={dir}>
                      {data.title}
                    </Typography>
                    <Divider className="my-16" />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={0.5}>
                      <Grid item xs={2}>
                        <Typography className="text-14 font-semibold m-0">
                          Time Stamp:
                        </Typography>
                      </Grid>
                      <Grid item xs={10}>
                        <Typography className="text-14 m-0">
                          {data.start_minute}:
                          {data.start_second < 10
                            ? `0${data.start_second}`
                            : data.start_second}{" "}
                          - {data.end_minute}:
                          {data.end_second < 10
                            ? `0${data.end_second}`
                            : data.end_second}{" "}
                          (from trimmed media)
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4} lg={2}>
                        <Typography className="text-14 font-semibold m-0">
                          Content:
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={8} lg={10}>
                        <Typography
                          className={`${tl} wysiwyg m-0`}
                          dir={dir}
                          dangerouslySetInnerHTML={{ __html: data.content }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </>
        )}

        {data.questions.length !== 0 && (
          <>
            <Typography className="text-16 font-bold p-16 px-32 pb-0">
              Interactive Questions:
            </Typography>
            {data.questions.map((data, index) => (
              <Paper
                key={data.title + index}
                elevation={0}
                variant="outlined"
                square
                className="p-16 pb-32 m-16"
              >
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography className={`${tl} font-bold m-0`} dir={dir}>
                      {data.title}
                    </Typography>
                    <Divider className="my-16" />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={0.5}>
                      <Grid item xs={2}>
                        <Typography className="text-14 font-semibold m-0">
                          Question Type
                        </Typography>
                      </Grid>
                      <Grid item xs={10}>
                        <Typography className="text-14 m-0">
                          {data.template_id === 0 && (
                            <>Audio Statement Completion</>
                          )}
                          {data.template_id === 1 && <>Audio True/False</>}
                          {data.template_id === 2 && <>Audio Multiple Choice</>}
                          {data.template_id === 3 && <>Focus Point Check</>}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography className="text-14 font-semibold m-0">
                          Time Stamp:
                        </Typography>
                      </Grid>
                      <Grid item xs={10}>
                        <Typography className="text-14 m-0">
                          {data.start_minute}:
                          {data.start_second < 10
                            ? `0${data.start_second}`
                            : data.start_second}{" "}
                          - {data.end_minute}:
                          {data.end_second < 10
                            ? `0${data.end_second}`
                            : data.end_second}{" "}
                          (from trimmed media)
                        </Typography>
                      </Grid>

                      <Divider className="my-16" />

                      <Grid item xs={2}>
                        {data.content.prompt_audio && (
                          <BASESvgIcon
                            className="text-green-black mx-2"
                            size={16}
                          >
                            heroicons-solid:volume-up
                          </BASESvgIcon>
                        )}
                        <Typography className="text-14 font-semibold m-0">
                          Prompt:
                        </Typography>
                      </Grid>
                      <Grid item xs={10}>
                        <Typography
                          className={`${tl} m-0 wysiwyg`}
                          dir={dir}
                          dangerouslySetInnerHTML={{
                            __html: data.content.prompt,
                          }}
                        />
                      </Grid>
                      {data.template_id !== 3 ? (
                        <>
                          {data.content.lures.map((lure, index) => (
                            <Grid
                              key={`lure-${lure.lure_text}-${index}`}
                              item
                              xs={12}
                            >
                              {index > 0 && <Divider className="mt-2 mb-8" />}
                              <Grid container spacing={0} columns={24}>
                                <Grid item xs={4}>
                                  {lure.is_correct ? (
                                    <Grid container direction="row">
                                      {lure.use_audio && (
                                        <BASESvgIcon
                                          className="text-green-black mx-2"
                                          size={16}
                                        >
                                          heroicons-solid:volume-up
                                        </BASESvgIcon>
                                      )}
                                      <BASESvgIcon
                                        className="text-green-600 mx-2"
                                        size={20}
                                      >
                                        heroicons-solid:check-circle
                                      </BASESvgIcon>
                                      <Typography className="text-14 font-semibold m-0">
                                        Correct
                                      </Typography>
                                    </Grid>
                                  ) : (
                                    <Grid container direction="row">
                                      {lure.use_audio && (
                                        <BASESvgIcon
                                          className="text-green-black mx-2"
                                          size={16}
                                        >
                                          heroicons-solid:volume-up
                                        </BASESvgIcon>
                                      )}
                                      <BASESvgIcon
                                        className="text-red-600 mx-2"
                                        size={20}
                                      >
                                        heroicons-solid:x-circle
                                      </BASESvgIcon>
                                      <Typography className="text-14 font-semibold m-0">
                                        Incorrect
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>
                                <Grid item xs>
                                  <Typography
                                    className={`${tl} m-0 ${
                                      index < data.content.lures.length - 1 &&
                                      `mb-8`
                                    }`}
                                    dir={dir}
                                  >
                                    {lure.lure_text}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                          ))}

                          <Grid item xs={2}>
                            <Typography className="text-14 font-semibold m-0">
                              Correct Feedback:
                            </Typography>
                          </Grid>
                          <Grid item xs={10}>
                            <Typography
                              className={`${tl} m-0 wysiwyg`}
                              dir={dir}
                              dangerouslySetInnerHTML={{
                                __html: data.content.correct_feedback,
                              }}
                            />
                          </Grid>

                          <Grid item xs={2}>
                            <Typography className="text-14 font-semibold m-0">
                              Incorrect Feedback:
                            </Typography>
                          </Grid>
                          <Grid item xs={10}>
                            <Typography
                              className={`${tl} m-0 wysiwyg`}
                              dir={dir}
                              dangerouslySetInnerHTML={{
                                __html: data.content.incorrect_feedback,
                              }}
                            />
                          </Grid>
                        </>
                      ) : (
                        <>
                          <Grid item xs={2}>
                            <Typography className="text-14 font-semibold m-0">
                              Suggested Feedback:
                            </Typography>
                          </Grid>
                          <Grid item xs={10}>
                            <Typography
                              className={`${tl} m-0 wysiwyg`}
                              dir={dir}
                              dangerouslySetInnerHTML={{
                                __html: data.content.suggested_feedback,
                              }}
                            />
                          </Grid>

                          <Grid item xs={2}>
                            <Typography className="text-14 font-semibold m-0">
                              Focus Point:
                            </Typography>
                          </Grid>
                          <Grid item xs={10}>
                            <Typography
                              className={`${tl} m-0 wysiwyg`}
                              dir={dir}
                              dangerouslySetInnerHTML={{
                                __html: data.content.focus_point,
                              }}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </>
        )}

        {data.eMentor.length !== 0 && (
          <EMentor
            eMentor={data.eMentor}
            tl={tl}
            dir={dir}
            componentCommentsCount={componentCommentsCount}
          />
        )}
      </div>
    </>
  );
};

export default PEInteractiveListening;
